import { useCallback, useEffect } from "react";
import { toast } from "react-toastify";
import { useAsyncTask } from "src-next/shared/utils/useAsyncTask";
import { useTeleconsultoriaProfile } from "../../profile/TeleconsultoriaProfileContext";
import {
  countUpcomingFreeSlots,
  listTeleconsultoriaAgendas,
  updateAgenda,
} from "../services/agendaService";
import type { AgendaEditValues, TeleconsultoriaAgendaSummary } from "../types";

interface AgendasData {
  agendas: TeleconsultoriaAgendaSummary[];
  /** Horários livres nos próximos dias, por agenda (undefined = sem contagem). */
  freeSlotsById: Record<string, number | undefined>;
}

const EMPTY_DATA: AgendasData = { agendas: [], freeSlotsById: {} };

async function loadAgendas(params: { organizationToken: string; practitionerReference?: string }) {
  // A tela mostra só agendas ativas; o serviço segue devolvendo todas para outros usos.
  const agendas = (await listTeleconsultoriaAgendas(params)).filter((agenda) => agenda.isActive);
  const counts = await Promise.all(
    agendas.map((agenda) => (agenda.isActive ? countUpcomingFreeSlots(agenda.id) : undefined)),
  );
  const freeSlotsById: AgendasData["freeSlotsById"] = {};
  agendas.forEach((agenda, agendaIndex) => {
    freeSlotsById[agenda.id] = counts[agendaIndex];
  });
  return { agendas, freeSlotsById };
}

/**
 * Agendas de teleconsultoria ATIVAS da PRESTADORA.
 * `practitionerReference`: só as do profissional (visão profissional); sem ele, todas.
 */
export function useTeleconsultoriaAgendas(practitionerReference?: string) {
  const { profile } = useTeleconsultoriaProfile();
  const organizationToken = profile.kind === "provider" ? profile.organizationToken : "";

  const loadTask = useAsyncTask({
    task: useCallback(
      () => loadAgendas({ organizationToken, practitionerReference }),
      [organizationToken, practitionerReference],
    ),
    initialData: EMPTY_DATA,
  });
  const updateTask = useAsyncTask({ task: updateAgenda });

  const { run } = loadTask;
  const reload = useCallback(() => {
    void run().catch((error) => console.error("useTeleconsultoriaAgendas", error));
  }, [run]);

  useEffect(() => {
    if (organizationToken) reload();
  }, [organizationToken, reload]);

  const { run: runUpdate } = updateTask;
  const update = useCallback(
    async (agenda: TeleconsultoriaAgendaSummary, values: AgendaEditValues): Promise<boolean> => {
      try {
        await runUpdate({ summary: agenda, values });
        toast.success("Agenda atualizada.");
        reload();
        return true;
      } catch (error) {
        console.error("useTeleconsultoriaAgendas.update", error);
        toast.error("Não foi possível salvar a agenda.");
        return false;
      }
    },
    [runUpdate, reload],
  );

  const data = loadTask.data ?? EMPTY_DATA;
  return {
    agendas: data.agendas,
    freeSlotsById: data.freeSlotsById,
    isLoading: loadTask.loading,
    hasError: loadTask.status === "error",
    isSaving: updateTask.loading,
    reload,
    update,
  };
}
